import { PropsWithChildren, ReactNode, useEffect } from "react";

import {
  HttpError,
  useGo,
  useInvalidate,
  useParsed,
  useResourceParams,
} from "@refinedev/core";
import { UseTableReturnType } from "@refinedev/react-table";
import _ from "lodash";
import { MoreHorizontal } from "lucide-react";

import { BadgeList } from "@/components/common/badge-list";
import { SearchBar } from "@/components/fields/search-bar";
import { CreateButton } from "@/components/refine-ui/buttons/create";
import { SaveTableViewModal } from "@/components/refine-ui/data-table/save-table-view-modal";
import { useStatePersistence } from "@/components/refine-ui/data-table/use-state-persistence";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { ClearTagModal } from "@/pages/events/features/patch-management/clear-tags-modal";

type DataTableActionBarProps = PropsWithChildren<{
  resource: string;
  table: UseTableReturnType<any, HttpError>;
  canCreate?: boolean;
  className?: string;
  /** Extra entries for the "⋯" menu, for actions only one resource has. */
  menuItems?: ReactNode;
}>;

export const DataTableActionBar = ({
  canCreate,
  resource: resourceFromProps,
  table,
  className,
  children,
  menuItems,
}: DataTableActionBarProps) => {
  const { resource, identifier } = useResourceParams({
    resource: resourceFromProps,
  });
  const resourceName = identifier ?? resource?.name;
  const isCreateButtonVisible = canCreate ?? !!resource?.create;

  const go = useGo();
  const parsed = useParsed();
  const invalidate = useInvalidate();
  const { addView, views, currentView, selectView, deleteView } =
    useStatePersistence({
      resource: resourceName!,
      table,
    });

  useEffect(() => {
    const globalFilter = table.reactTable.getState().globalFilter;
    const urlQuery = parsed.params?.query;
    const urlQueryBy = parsed.params?.queryBy;

    if (globalFilter && !_.isEmpty(globalFilter)) {
      if (globalFilter.includes(":")) {
        const [queryBy, query] = globalFilter.split(":");
        handleSearch(query, queryBy.replace(":", ""), false);
      } else {
        handleSearch(globalFilter, undefined, false);
      }
    } else if (typeof urlQuery === "string" && urlQuery !== "") {
      // The url already holds a search: a reload, or a trip to another view and back, lands here
      // with an empty table. The url is what the list fetches by, so it stays as it is, and the
      // box is seeded from it. Writing an empty query here would erase the curator's search.
      const queryBy =
        typeof urlQueryBy === "string" && urlQueryBy !== ""
          ? urlQueryBy
          : undefined;
      table.reactTable.setGlobalFilter(
        queryBy ? `${queryBy}:${urlQuery}` : urlQuery,
      );
    } else {
      handleSearch("", undefined, false);
    }
  }, []);

  const handleSearch = (
    query: string,
    queryBy?: string,
    setGlobal: boolean = true,
  ) => {
    if (setGlobal)
      table.reactTable.setGlobalFilter(queryBy ? `${queryBy}:${query}` : query);
    go({
      query: {
        query,
        queryBy: queryBy,
      },
      type: "replace",
      options: {
        keepQuery: true,
      },
    });
  };

  return (
    <div className="flex flex-col gap-2">
      <div className={cn("flex flex-row space-x-1 items-center", className)}>
        <SearchBar
          defaultValue={table.reactTable.getState().globalFilter ?? ""}
          handleSearch={handleSearch}
          suggestions={
            (table.reactTable.options.meta as any)?.querySuggestions ?? []
          }
        />
        {children}
        <div className="flex-1" />
        <Button
          variant={"ghost"}
          size={"sm"}
          onClick={() => table.refineCore.setFilters([], "replace")}
          className="text-foreground"
        >
          Clear Filters
        </Button>
        <Button
          variant={"ghost"}
          size={"sm"}
          onClick={() =>
            invalidate({
              resource: resource?.name ?? "",
              invalidates: ["list"],
            })
          }
          className="text-foreground items-center space-x-1"
        >
          {/* <RefreshCwIcon size={16} /> */}
          Refresh
        </Button>
        {isCreateButtonVisible && <CreateButton size="sm" newTab />}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="flex flex-col">
            {/* <Button
            size="sm"
            variant={"ghost"}
            onClick={() => {
              const state = table.reactTable.getState();
              localStorage.setItem(
                `${resourceName}-list-state`,
                JSON.stringify(state),
              );
            }}
          >
            Save State
          </Button>
          <Button
            size="sm"
            variant={"ghost"}
            className="bg-destructive"
            onClick={() =>
              localStorage.removeItem(`${resourceName}-list-state`)
            }
          >
            Clear State
          </Button> */}
            <ClearTagModal />
            {menuItems}
            <SaveTableViewModal onSaved={addView} />
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div
        className="flex flex-row mb-2 items-center gap-2"
        hidden={views.length === 0}
      >
        <Label>Saved Views:</Label>
        <BadgeList
          items={views.map((v) => ({ id: v.id, label: v.name }))}
          selectedId={currentView?.id}
          onSelect={selectView}
          onDelete={deleteView}
        />
      </div>
    </div>
  );
};
